import React from "react";
import { SummaryExchange } from "../types/summary";

type NotificationItem = {
  id: number;
  title: string;
  body: string;
  category: string;
  severity: string;
  created_at: string;
  is_read: boolean;
};

interface RightPanelProps {
  width: number;
  isCollapsed: boolean;
  onToggle: () => void;
  onResizeStart: (e: React.MouseEvent) => void;
  rightPanelRef: React.RefObject<HTMLDivElement | null>;
  mode: "summary" | "notifications" | null;
  notifications: NotificationItem[];
  notificationsLoading: boolean;
  notificationsError: string | null;
  onRefreshNotifications: () => void;
  summaryHistory: SummaryExchange[];
  summaryLoading: boolean;
  summaryError: string | null;
  summarizationEnabled?: boolean;

}

const Pill: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <span
    style={{
      display: "inline-flex",
      alignItems: "center",
      padding: "2px 6px",
      borderRadius: 999,
      background: "#EEF2FF",
      color: "#4338CA",
      fontSize: 11,
      marginRight: 4,
      marginBottom: 4,
    }}
  >
    {children}
  </span>
);

const RightPanel: React.FC<RightPanelProps> = ({
  width,
  isCollapsed,
  onToggle,
  onResizeStart,
  rightPanelRef,
  mode,
  notifications,
  notificationsLoading,
  notificationsError,
  onRefreshNotifications,
  summaryHistory,
  summaryLoading,
  summaryError,
  summarizationEnabled = true,

}) => {
  const renderNotifications = () => {
    if (notificationsLoading) {
      return <div style={{ fontSize: 12, color: "#6b7280" }}>Loading notifications…</div>;
    }
    if (notificationsError) {
      return (
        <div style={{ padding: 10, borderRadius: 10, background: "#FEF3C7", border: "1px solid #FCD34D", color: "#92400E" }}>
          Notifications are temporarily unavailable. Click refresh to try again.
        </div>
      );
    }
    if (!notifications.length) {
      return (
        <div style={{ padding: 12, borderRadius: 10, background: "#F3F4F6", border: "1px dashed #E5E7EB" }}>
          <strong style={{ display: "block", fontSize: 12, color: "#111827" }}>No notifications yet</strong>
          <span style={{ fontSize: 11, color: "#6b7280" }}>Stay tuned — we’ll surface important HR events here.</span>
        </div>
      );
    }
    return (
      <ul style={{ listStyle: "none", padding: 0, margin: 0, display: "flex", flexDirection: "column", gap: 8 }}>
        {notifications.map((notification) => (
          <li key={notification.id} style={{ padding: 10, borderRadius: 8, border: "1px solid #E5E7EB" }}>
            <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 4 }}>
              <strong style={{ fontSize: 12 }}>{notification.title}</strong>
              <span style={{ fontSize: 10, color: "#9CA3AF" }}>
                {new Date(notification.created_at).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}
              </span>
            </div>
            <div style={{ fontSize: 12, color: "#374151" }}>{notification.body}</div>
            <div style={{ marginTop: 6 }}>
              <Pill>{notification.category}</Pill>
              <Pill>{notification.severity}</Pill>
            </div>
          </li>
        ))}
      </ul>
    );
  };

  const renderSummary = () => {
    if (!summaryHistory.length) {
      return (
        <div style={{ padding: 12, borderRadius: 10, border: "1px solid #E5E7EB", background: "#F9FAFB" }}>
          <div style={{ fontSize: 12, color: "#6b7280" }}>
            Ask for a summary to see your instruction paired with a professional recap.
          </div>
        </div>
      );
    }

    return (
      <div style={{ display: "flex", flexDirection: "column", gap: 16, paddingBottom: 4 }}>
        {summaryHistory.map((entry) => (
          <div key={entry.id} style={{ display: "flex", flexDirection: "column", gap: 8 }}>
            {/* Summary content box - no redundant instruction box since heading already shows "Activity Summary" */}
            <div
              style={{
                alignSelf: "flex-start",
                width: "100%",
                background: "#FFFFFF",
                borderRadius: 12,
                padding: "12px 14px",
                border: "1px solid #E5E7EB",
                boxShadow: "0 1px 3px rgba(15,23,42,0.08)",
              }}
            >
              {entry.status === "pending" && (
                <div style={{ fontSize: 12, color: "#6b7280" }}>Generating a professional summary…</div>
              )}
              {entry.status === "error" && (
                <div style={{ fontSize: 12, color: "#b91c1c" }}>{entry.error || "Summary unavailable."}</div>
              )}
              {entry.status === "ready" && entry.summary && (
                <>
                  <div style={{ fontSize: 13, color: "#111827", lineHeight: 1.5, marginBottom: 8 }}>
                    {entry.summary.overview || "No overview available."}
                  </div>
                  {entry.summary.summary_points && entry.summary.summary_points.length ? (
                    <ul style={{ margin: 0, paddingLeft: 18, color: "#374151" }}>
                      {entry.summary.summary_points.map((point, index) => (
                        <li key={`${entry.id}-point-${index}`} style={{ fontSize: 12, marginBottom: 4 }}>
                          {point}
                        </li>
                      ))}
                    </ul>
                  ) : (
                    <div style={{ fontSize: 12, color: "#9CA3AF" }}>No bullet points generated.</div>
                  )}
                </>
              )}
            </div>
          </div>
        ))}
      </div>
    );
  };

  return (
    <div
      ref={rightPanelRef}
      style={{
        width: isCollapsed ? "0px" : `${width}px`,
        transition: "width 0.3s ease",
        borderLeft: "1px solid #ddd",
        overflow: "hidden",
        position: "relative",
        height: "100%",
      }}
    >
      {/* Collapse toggle button */}
      <button
        onClick={onToggle}
        style={{
          position: "absolute",
          left: "-20px",
          top: "50%",
          transform: "translateY(-50%)",
          background: "#fff",
          border: "1px solid #ccc",
          borderRadius: "50%",
          cursor: "pointer",
          padding: "4px 6px",
        }}
      >
        {isCollapsed ? "«" : "»"}
      </button>

      {/* Resize handle */}
      <div
        onMouseDown={onResizeStart}
        style={{
          width: "5px",
          cursor: "col-resize",
          position: "absolute",
          left: 0,
          top: 0,
          bottom: 0,
          zIndex: 10,
        }}
      />

      {/* Panel content */}
      {!isCollapsed && (
        <div
          style={{
            padding: "16px",
            height: "100%",
            background: "#ffffff",
            display: "flex",
            flexDirection: "column",
          }}
        >
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <h3 style={{ margin: 0, fontSize: 14, fontWeight: 600, color: "#111827" }}>
              {mode === "notifications"
                ? "Notifications"
                : mode === "summary"
                  ? "Activity Summary"
                  : "Assistant panel"}
            </h3>
            <button onClick={onToggle} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#6b7280', padding: '4px' }}>✕</button>
          </div>
          <p style={{ marginTop: 4, fontSize: 12, color: "#6b7280" }}>
            {mode === "notifications"
              ? "Recent system events and alerts will appear here."
              : mode === "summary"
                ? "Summary of your recent conversations and activities."
                : "Use the header icons to open notifications or summary."}
          </p>

          {/* Dynamic summarization status indicator */}
          {mode === "summary" && !summarizationEnabled && (
            <p style={{ marginTop: 4, fontSize: 11, color: "#d97706", fontWeight: 500 }}>
              Last updated summary: {new Date(summaryHistory[0].requestedAt).toLocaleString()}
            </p>
          )}
          {mode === "summary" && summarizationEnabled && summaryHistory.length > 0 && summaryHistory[0]?.requestedAt && (
            <p style={{ marginTop: 4, fontSize: 11, color: "#3b82f6", fontWeight: 500 }}>
              Latest Summary.
            </p>
          )}

          <div style={{ marginTop: 12, paddingTop: 8, borderTop: "1px solid #e5e7eb", flex: 1, display: "flex", flexDirection: "column", minHeight: 0 }}>
            {mode === "notifications" && (
              <>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 8 }}>
                  <span style={{ fontSize: 12, color: "#6b7280" }}>Latest notifications</span>
                  <button
                    onClick={onRefreshNotifications}
                    style={{
                      padding: "6px 12px",
                      borderRadius: 999,
                      border: "1px solid #d1d5db",
                      background: "#fff",
                      cursor: "pointer",
                      fontSize: 11,
                      fontWeight: 600,
                    }}
                  >
                    Refresh
                  </button>
                </div>
                <div style={{ flex: 1, overflowY: "auto" }}>{renderNotifications()}</div>
              </>
            )}

            {mode === "summary" && (
              <>
                <div style={{ flex: 1, overflowY: "auto", paddingRight: 4 }}>{renderSummary()}</div>
                {summaryError && (
                  <div style={{ fontSize: 11, color: "#b91c1c", marginTop: 8 }}>{summaryError}</div>
                )}
              </>
            )}
          </div>
        </div>
      )
      }
    </div >
  );
};

export default RightPanel;
